import { Shop } from '@/features/product/choose-pickup-point/types/shop'

import { CatalogDiscountProductsData } from '@/entities/catalog-discount/schema/catalog-discount.schema'
import { DiscountCategoriesMap } from '@/entities/catalog-discount/schema/discount-categories.schema'
import { FilterData } from '@/entities/catalog-discount/schema/filter.schema'

import { apiSlice } from '@/shared/api/apiSlice'
import { TServerSection } from '@/shared/types/deprecated-types'

interface GetDiscountSectionProductsProps {
	params?: Record<string, string>
	regionCookies?: string
	filter?: string
}

interface GetCatalogDiscountDataProps {
	categoryCode: string
	resolvedUrl: string
	regionCookies?: string
}

interface GetCatalogDiscountFilterUrl {
	categoryCode: string
	filterUrl: string
}

interface GetCatalogDiscountCategoriesProps {
	regionCookies?: string
}

interface GetDiscountDetailedSectionProps {
	category: string
	regionCookies?: string
}

export const catalogDiscountApiSlice = apiSlice.injectEndpoints({
	endpoints: build => ({
		getCatalogDiscountFilter: build.query<FilterData, GetCatalogDiscountDataProps>({
			query: ({ categoryCode, resolvedUrl, regionCookies = '' }) => ({
				url: `discount/filter/${categoryCode || ''}`,
				params: {
					['sef-url']: resolvedUrl
				},
				headers: {
					Cookie: regionCookies
				}
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.categoryCode}-${queryArgs.resolvedUrl}`
			},
			transformResponse: (result: { data: FilterData }) => result.data
		}),
		getCatalogDiscountFilterUrl: build.query<string, GetCatalogDiscountFilterUrl>({
			query: ({ categoryCode, filterUrl }) => ({
				url: `discount/filter/${categoryCode || ''}/sef-url`,
				params: {
					filter: filterUrl,
					['sort']: `order=price&direction=asc`
				}
			}),
			transformResponse: (result: { data: string }) => result.data
		}),
		getDiscountCategories: build.query<DiscountCategoriesMap, GetCatalogDiscountCategoriesProps>({
			query: ({ regionCookies = '' }) => ({
				url: `discount/categories`,
				headers: {
					Cookie: regionCookies
				}
			}),
			transformResponse: (result: { data: DiscountCategoriesMap }) => result.data
		}),
		getDiscountDetailedSection: build.query<TServerSection, GetDiscountDetailedSectionProps>({
			query: ({ category, regionCookies = '' }) => ({
				url: `discount/filter/${category}`,
				headers: {
					Cookie: regionCookies
				}
			}),
			transformResponse: (result: { data: TServerSection }) => result.data
		}),
		getDiscountSectionProducts: build.query<CatalogDiscountProductsData, GetDiscountSectionProductsProps>({
			query: ({ params = {}, filter, regionCookies = '' }) => {
				const { order = `price`, ...restParams } = params

				return {
					url: `discount/products`,
					params: {
						filter: filter,
						...restParams,
						order,
						direction: `asc`
					},
					headers: {
						Cookie: regionCookies
					},
					cache: `no-store`
				}
			},
			keepUnusedDataFor: 0,
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${JSON.stringify(queryArgs.params)}-${queryArgs.filter}`
			},
			transformResponse: (result: { data: CatalogDiscountProductsData }): CatalogDiscountProductsData => ({
				...result.data,
				ITEMS: result.data.ITEMS ?? []
			})
		}),
		getDiscountProductStores: build.query<Shop[], string>({
			query: productId => ({
				url: `discount/products/${productId}/stores`
			}),
			transformResponse: (result: { data: Shop[] }) => result.data ?? []
		})
	})
})

export const {
	useGetCatalogDiscountFilterQuery,
	useGetCatalogDiscountFilterUrlQuery,
	useLazyGetCatalogDiscountFilterUrlQuery,
	useGetDiscountCategoriesQuery,
	useLazyGetCatalogDiscountFilterQuery,
	useLazyGetDiscountCategoriesQuery,
	useGetDiscountDetailedSectionQuery,
	useLazyGetDiscountDetailedSectionQuery,
	useGetDiscountSectionProductsQuery,
	useLazyGetDiscountSectionProductsQuery,
	useGetDiscountProductStoresQuery,
	useLazyGetDiscountProductStoresQuery
} = catalogDiscountApiSlice
